import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = 'https://gmc-validator-api.onrender.com';
const API = `${BACKEND_URL}/api`;

const FREE_USES_LIMIT = 5;
// CRITICAL UPDATE: LIVE STRIPE PAYMENT LINK INSERTED HERE
const STRIPE_PAYMENT_URL = 'https://buy.stripe.com/eVq5kE2w8f1CdQ2bi6gQE00'; 

const ValidatorPage = () => {
  const [formData, setFormData] = useState({
    size: '',
    color: '',
    item_group_id: '',
    gender: '',
    age_group: ''
  });
  const [outputText, setOutputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [usesRemaining, setUsesRemaining] = useState(FREE_USES_LIMIT);
  const [isPro, setIsPro] = useState(false);

  useEffect(() => {
    // Check local storage for usage count and pro status
    const storedUses = localStorage.getItem('gmc_uses');
    const proStatus = localStorage.getItem('is_pro_user');
    
    if (proStatus === 'true') {
      setIsPro(true);
      setUsesRemaining(Infinity);
    } else {
      const uses = storedUses ? parseInt(storedUses) : 0;
      setUsesRemaining(FREE_USES_LIMIT - uses);
    }
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleValidate = async () => {
    // Check if user has exceeded free uses
    if (!isPro && usesRemaining <= 0) {
      toast.error('Free validation limit reached! Please upgrade.');
      return;
    }

    // Basic validation - check if at least gender and age_group are provided (frontend check)
    if (!formData.gender.trim() || !formData.age_group.trim()) {
      toast.error('Gender and Age Group are required fields');
      return;
    }

    setIsLoading(true);
    setOutputText('');

    try {
      // Call validation API with individual fields
      const response = await axios.post(`${API}/validate/apparel`, {
        size: formData.size.trim() || null,
        color: formData.color.trim() || null,
        item_group_id: formData.item_group_id.trim() || null,
        gender: formData.gender.trim(),
        age_group: formData.age_group.trim()
      });
      
      // Display result (Handles multi-error reporting as requested)
      if (response.data.status === 'OK') {
        setOutputText('✅ VALIDATION PASSED\n\nAll GMC apparel attributes are compliant. Your product is ready for upload!');
        toast.success('Validation passed!');
      } else {
        // Display all errors found
        const errorList = response.data.errors.map((err, idx) => `${idx + 1}. ❌ ${err}`).join('\n\n');
        setOutputText(`VALIDATION FAILED\n\nThe following issues were found. FIX THESE:\n\n${errorList}`);
        toast.error(`Found ${response.data.errors.length} validation issue(s)`);
      }

      // Update usage count
      if (!isPro) {
        const currentUses = parseInt(localStorage.getItem('gmc_uses') || '0');
        const newUses = currentUses + 1;
        localStorage.setItem('gmc_uses', newUses.toString());
        setUsesRemaining(FREE_USES_LIMIT - newUses);
      }

    } catch (error) {
      console.error('Validation error:', error);
      setOutputText('❌ ERROR\n\nFailed to validate attributes. Please check your network connection and try again.');
      toast.error('Validation request failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgrade = () => {
    // Redirect to the live Stripe Payment Link
    window.location.href = STRIPE_PAYMENT_URL;
  };

  // Dynamic button text based on pro status
  const getButtonText = () => {
    if (isLoading) return 'Validating...';
    if (isPro) return 'Validate Attributes (Unlimited Access)';
    if (usesRemaining <= 0) return 'Upgrade to Pro for Unlimited Access'; // Shows if limit reached
    return `Validate Attributes (${usesRemaining} Free Uses)`; // Default free user text
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 
            data-testid="main-title" 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-3"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            GMC Apparel Attribute Validator
          </h1>
          <h2 
            className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-700 mb-3"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            Stop Disapprovals. Guarantee Compliance. Resume Sales.
          </h2>
          <p className="text-base sm:text-lg text-gray-600" style={{ fontFamily: 'Inter, sans-serif' }}>
            Validate your Google Merchant Center apparel attributes instantly
          </p>
          
          {/* Usage Badge */}
          <div className="mt-4 inline-block">
            {isPro ? (
              <span className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-full text-sm font-semibold">
                ⭐ Pro User - Unlimited Validations
              </span>
            ) : (
              <span className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-semibold">
                {usesRemaining} Free Validations Remaining
              </span>
            )}
          </div>
        </div>

        {/* Main Content */}
        {!isPro && usesRemaining <= 0 ? (
          // Paywall
          <Card className="p-6 sm:p-8 text-center bg-white shadow-lg max-w-2xl mx-auto">
            <div className="mb-6">
              <div className="text-6xl mb-4">🔒</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Unlock Unlimited Validations
              </h2>
              <p className="text-gray-600 mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                You've used all your free validations. Upgrade to Pro for unlimited access.
              </p>
              <Button
                data-testid="upgrade-button"
                onClick={handleUpgrade}
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-full shadow-lg transition-all duration-300 hover:scale-105 w-full sm:w-auto"
              >
                Upgrade to Pro - One-Time Payment
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Why Check First Section */}
            <Card className="p-6 bg-white shadow-lg border-2 border-blue-100">
              <h3 className="text-xl font-bold text-gray-800 mb-4 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Why Check First?
              </h3>
              <p className="text-center text-gray-700 mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                Every hour your products are disapproved, you lose revenue. Use this tool to get the exact, actionable fix in seconds, guaranteed by Google's latest rules.
              </p>
              
              {/* Benefit Icons */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="flex flex-col items-center text-center">
                  <div className="flex items-center justify-center mb-3" style={{ height: '70px' }}>
                    <img 
                      src="https://customer-assets.emergentagent.com/job_apparel-check/artifacts/1b63720a_Stop%20Disapproval%20%20Ensure%20Compliance%20Icon.png" 
                      alt="Stop Disapproval" 
                      style={{ height: '70px', width: 'auto' }}
                      className="object-contain"
                    />
                  </div>
                  <p className="text-sm font-semibold text-gray-800">Stop Disapprovals</p>
                  <p className="text-xs text-gray-600">Ensure Compliance</p>
                </div>
                <div className="flex flex-col items-center text-center">
                  <div className="flex items-center justify-center mb-3" style={{ height: '70px' }}>
                    <img 
                      src="https://customer-assets.emergentagent.com/job_apparel-check/artifacts/vosx71q1_Save%20Time%20%20Instant%20Fix%20Icon.png" 
                      alt="Save Time" 
                      style={{ height: '70px', width: 'auto' }}
                      className="object-contain"
                    />
                  </div>
                  <p className="text-sm font-semibold text-gray-800">Save Time</p>
                  <p className="text-xs text-gray-600">Instant Fix</p>
                </div>
                <div className="flex flex-col items-center text-center">
                  <div className="flex items-center justify-center mb-3" style={{ height: '70px' }}>
                    <img 
                      src="https://customer-assets.emergentagent.com/job_apparel-check/artifacts/r2fad1mf_Boost%20Revenue%20%20Prevent%20Loss%20Icon.png" 
                      alt="Boost Revenue" 
                      style={{ height: '70px', width: 'auto' }}
                      className="object-contain"
                    />
                  </div>
                  <p className="text-sm font-semibold text-gray-800">Boost Revenue</p>
                  <p className="text-xs text-gray-600">Prevent Loss</p>
                </div>
              </div>
            </Card>

            {/* Input Section */}
            <Card className="p-4 sm:p-6 bg-white shadow-lg">
              <h2 
                className="text-lg sm:text-xl font-semibold text-gray-800 mb-4"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                Input Attributes
              </h2>
              <p className="text-sm text-gray-500 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
                Fill in the GMC apparel attributes. Fields marked with * are required.
              </p>

              <div className="space-y-4">
                {/* Size Field */}
                <div>
                  <Label htmlFor="size" className="text-sm font-medium text-gray-700 mb-1.5 block">
                    GMC Size Attribute
                  </Label>
                  <Input
                    id="size"
                    data-testid="input-size"
                    value={formData.size}
                    onChange={(e) => handleInputChange('size', e.target.value)}
                    placeholder="e.g., M/Regular, XL/Tall"
                    className="text-base border-2 border-gray-200 focus:border-blue-500 transition-colors"
                  />
                  <p className="text-xs text-gray-500 mt-1">Use slashes (/) for multi-dimensional sizes</p>
                </div>

                {/* Color Field */}
                <div>
                  <Label htmlFor="color" className="text-sm font-medium text-gray-700 mb-1.5 block">
                    GMC Color Attribute(s)
                  </Label>
                  <Input
                    id="color"
                    data-testid="input-color"
                    value={formData.color}
                    onChange={(e) => handleInputChange('color', e.target.value)}
                    placeholder="e.g., Blue/Red, Navy-Blue"
                    className="text-base border-2 border-gray-200 focus:border-blue-500 transition-colors"
                  />
                  <p className="text-xs text-gray-500 mt-1">Max 3 colors, separated by slashes (/)</p>
                </div>

                {/* Item Group ID Field */}
                <div>
                  <Label htmlFor="item_group_id" className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Item Group ID
                  </Label>
                  <Input
                    id="item_group_id"
                    data-testid="input-item-group-id"
                    value={formData.item_group_id}
                    onChange={(e) => handleInputChange('item_group_id', e.target.value)}
                    placeholder="e.g., SHIRT-001, ABC123"
                    className="text-base border-2 border-gray-200 focus:border-blue-500 transition-colors"
                  />
                  <p className="text-xs text-gray-500 mt-1">Required if both size and color are provided</p>
                </div>

                {/* Gender Field */}
                <div>
                  <Label htmlFor="gender" className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Gender <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="gender"
                    data-testid="input-gender"
                    value={formData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                    placeholder="Male, Female, or Unisex"
                    className="text-base border-2 border-gray-200 focus:border-blue-500 transition-colors"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Required: Male, Female, or Unisex</p>
                </div>

                {/* Age Group Field */}
                <div>
                  <Label htmlFor="age_group" className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Age Group <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="age_group"
                    data-testid="input-age-group"
                    value={formData.age_group}
                    onChange={(e) => handleInputChange('age_group', e.target.value)}
                    placeholder="Adult or Kids"
                    className="text-base border-2 border-gray-200 focus:border-blue-500 transition-colors"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Required: Adult or Kids</p>
                </div>
              </div>

              <Button
                data-testid="validate-button"
                onClick={handleValidate}
                disabled={isLoading || (!isPro && usesRemaining <= 0)} // Disable button if loading OR limit reached
                className="mt-6 w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-5 sm:py-6 text-base sm:text-lg rounded-lg shadow-md transition-all duration-300 hover:scale-[1.02]"
              >
                {getButtonText()}
              </Button>
            </Card>

            {/* Output Section */}
            {outputText && (
              <Card className="p-4 sm:p-6 bg-white shadow-lg">
                <label 
                  className="block text-lg sm:text-xl font-semibold text-gray-800 mb-3"
                  style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                >
                  Validation Result
                </label>
                <div
                  data-testid="output-box"
                  className="min-h-[150px] p-4 bg-gray-50 border-2 border-gray-200 rounded-lg whitespace-pre-wrap text-base font-mono"
                  style={{ fontFamily: 'monospace' }}
                >
                  {outputText}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Info Footer */}
        <div className="mt-8 text-center text-xs sm:text-sm text-gray-500" style={{ fontFamily: 'Inter, sans-serif' }}>
          <p>Validates: Size | Color | Item Group ID | Gender | Age Group</p>
        </div>
      </div>
    </div>
  );
};

export default ValidatorPage;
